import axios from 'axios/dist/axios';


const defaultState = {
    chapterTree: [],

    activeChapter: null,
    symptomsList: [],

    searchSymptomInputValue: '',
    searchSymptomResultList: [],
    isLoadingSearchResults: false,

    selectedMainSymptomCode: ''
};

// ======================================================================
// Initial State
// ======================================================================

const state = {
    chapterTree:                defaultState.chapterTree,

    activeChapter:              defaultState.activeChapter,
    symptomsList:               defaultState.symptomsList,

    searchSymptomInputValue:    defaultState.searchSymptomInputValue,
    searchSymptomResultList:    defaultState.searchSymptomResultList,
    isLoadingSearchResults:     defaultState.isLoadingSearchResults,

    selectedMainSymptomCode:      defaultState.selectedMainSymptomCode
};

// ======================================================================
// Getters
// ======================================================================

const getters = {
    getChapterTree: state => state.chapterTree,
    getHasSubchapters (state) {
        return (typeof state.chapterTree.hasSubChapters !== "undefined" && state.chapterTree.hasSubChapters === true) ? true : false;
    },

    getActiveChapter: state => state.activeChapter,
    getActiveChapterCode (state) {
        return (state.activeChapter !== null && typeof state.activeChapter.code !== "undefined") ? state.activeChapter.code : null;
    },
    getSymptomsList: state => state.symptomsList,

    getSearchSymptomInputValue: state => state.searchSymptomInputValue,
    getSearchSymptomResultList: state => state.searchSymptomResultList,
    getIsLoadingSearchResults: state => state.isLoadingSearchResults,


    getSelectedMainSymptomCode: state => state.selectedMainSymptomCode,
    isMainSymptomSelected (state) {
        return (state.selectedMainSymptomCode !== '') ? true : false;
    }
};

// ======================================================================
// Actions
// ======================================================================

const actions = {
    loadChapterTree ({ commit, dispatch }) {
        let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/chapterTree');
        axiosRequest.then((response) => {
            if (typeof response.data.data !== "undefined") {
                commit('resetStore');
                if (typeof response.data.data.CHAPTER_TREE !== "undefined") {
                    commit('setChapterTree', response.data.data.CHAPTER_TREE);
                }
                if (typeof response.data.data.MAIN_SYMPTOM !== "undefined" && typeof response.data.data.MAIN_SYMPTOM.code !== "undefined") {
                    commit("setSelectedMainSymptomCode", response.data.data.MAIN_SYMPTOM.code);
                    dispatch("expandChapterTreeToSelectedMainSymptom");
                }
                if (typeof response.data.data.START_QUESTIONS !== "undefined") {
                    commit('AssessmentStartStore/setStartQuestionsData', response.data.data.START_QUESTIONS, {root: true});
                }
                if (typeof response.data.data.PATIENT_DATA !== "undefined") {
                    commit('AssessmentStartStore/setPatientProfileData', response.data.data.PATIENT_DATA, {root: true});
                }
            }

            dispatch("AssessmentAlertsStore/refreshAlerts", response.data, { root: true });
        });
        axiosRequest.catch((error) => {
            console.log(error);
        });
    },


    expandChapterTreeToSelectedMainSymptom ({ commit, getters }) {
        let chapterTree = getters.getChapterTree;
        let selectedSymptomCode = getters.getSelectedMainSymptomCode;
        let activeChapterIndex = -1;

        if (chapterTree.hasSubChapters === true) {
            // ------- Has chapters and subchapters.
            activeChapterIndex = chapterTree.chapters.findIndex((crtChapter) => {
                let activeSubchapterIndex = crtChapter.subChapters.findIndex((crtSubchapter) => {
                    let activeSymptomIndex = crtSubchapter.symptoms.findIndex((crtSymptom) => {
                        return (crtSymptom.code === selectedSymptomCode) ? true : false;
                    });
                    return (activeSymptomIndex !== -1) ? true : false;
                });
                return (activeSubchapterIndex !== -1) ? true : false;
            });
        } else {
            // ------- Has chapters only, no subchapters.
            activeChapterIndex = chapterTree.chapters.findIndex((crtChapter) => {
                let activeSymptomIndex = crtChapter.symptoms.findIndex((crtSymptom) => {
                    return (crtSymptom.code === selectedSymptomCode) ? true : false;
                });
                return (activeSymptomIndex !== -1) ? true : false;
            });
        }

        if (activeChapterIndex !== -1) {
            commit("setActiveChapter", chapterTree.chapters[activeChapterIndex]);
        }
        else {
            commit("clearActiveChapter");
        }
    },


    submitSearchSymptom ({ commit }, searchedText) {

        let promise = new Promise((resolve, reject) => {
            if (!searchedText) {
                // Input has been cleared, just empty the result list.
                commit('setSearchSymptomResultList', []);
                resolve([]);
            }
            else {
                // Execute an ajax request to the server in order to get search results.
                commit("setIsLoadingSearchResults", true);
                commit("clearSearchSymptomResultList");
                let axiosRequest = axios.post(process.env.API_BASE_URL_PATHFINDER + '/assessment/searchSymptom', {
                    "searchText": searchedText
                });
                axiosRequest.then((response) => {
                    // Success.
                    commit('setSearchSymptomResultList', response.data.data.SEARCHED_SYMPTOMS.symptoms);
                    resolve(response);
                });
                axiosRequest.catch((error) => {
                    // Fail.
                    console.log(error);
                    reject(error);
                });
                axiosRequest.then(() => {
                    // Always (it's a second then, coming after catch).
                    commit("setIsLoadingSearchResults", false);
                });
            }
        });

        return promise;
    },

    resetSearchSymptom ({commit}) {
        commit('setSearchSymptomInputValue', ''); // Clear Search Symptom input
        commit('setSearchSymptomResultList', []); // Clear Search results list.
    },

    setSelectedMainSymptomCode ({ commit }, newValue) {
        commit("setSelectedMainSymptomCode", newValue);
    }
};


// ======================================================================
// Mutations
// ======================================================================

const mutations = {
    setChapterTree (state, chapterTree) {
        state.chapterTree = chapterTree;
    },

    setActiveChapter (state, chapterObj) {
        state.activeChapter = chapterObj;
    },
    clearActiveChapter (state) {
        state.activeChapter = null;
    },

    setSymptomsList (state, symptomsList) {
        state.symptomsList = symptomsList;
    },

    setSearchSymptomInputValue(state, textValue){
        state.searchSymptomInputValue = textValue;
    },
    setSearchSymptomResultList(state, newValue){
        state.searchSymptomResultList = newValue;
    },
    clearSearchSymptomResultList(state, newValue){
        state.searchSymptomResultList = defaultState.searchSymptomResultList;
    },
    setIsLoadingSearchResults(state, newValue){
        state.isLoadingSearchResults = newValue;
    },

    setSelectedMainSymptomCode(state, newValue){
        state.selectedMainSymptomCode = newValue;
    },
    clearSelectedMainSymptomCode(state){
        state.selectedMainSymptomCode = '';
    },


    // ----------------------------------------------------------

    resetStore(state) {
        state.chapterTree                 = defaultState.chapterTree;

        state.activeChapter               = defaultState.activeChapter;
        state.symptomsList                = defaultState.symptomsList;

        state.searchSymptomInputValue     = defaultState.searchSymptomInputValue;
        state.searchSymptomResultList     = defaultState.searchSymptomResultList;
        state.isLoadingSearchResults      = defaultState.isLoadingSearchResults;

        state.selectedMainSymptomCode     = defaultState.selectedMainSymptomCode;
    }
};


// ======================================================================
// Exports
// ======================================================================

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};



// WEBPACK FOOTER //
// ./src/common/store/modules/pathfinder/standard-forms/PfMainSymptomStore.js